/**
 * Screen Wake Lock: keeps the screen on while music is playing.
 *
 * Playback runs through the YouTube iframe player, which stops as soon as a
 * mobile browser backgrounds the page. That rule cannot be turned off, so the
 * only way to keep the music going is to never go to the background: with the
 * screen held on, the app stays in front and playback simply continues.
 *
 * It is opt-in because holding the screen on costs battery. The browser drops
 * the lock whenever the page is hidden, so it has to be taken again on return.
 */

const STORAGE_KEY = "sonlyo.keepawake";

let sentinel = null;

export function isSupported() {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}

export function isEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setEnabled(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* private mode */
  }
}

let onLost = null;

/** Called whenever the system takes the lock back, so it can be re-requested. */
export function setOnLost(callback) {
  onLost = callback;
}

export async function acquire() {
  if (!isSupported() || sentinel) return false;
  try {
    sentinel = await navigator.wakeLock.request("screen");
    sentinel.addEventListener("release", () => {
      sentinel = null;
      // Android drops the lock on its own, for instance after the screen has
      // been off and comes back. Without re-requesting it, the setting quietly
      // stops working after the first interruption.
      if (onLost) onLost();
    });
    return true;
  } catch {
    // Refused when the page is hidden or the device is low on battery.
    sentinel = null;
    return false;
  }
}

export async function release() {
  if (!sentinel) return;
  try {
    await sentinel.release();
  } catch {
    /* already gone */
  }
  sentinel = null;
}

export function isHeld() {
  return !!sentinel;
}
